import { type Editor, posToDOMRect } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { BubbleMenu } from "@tiptap/react/menus";
import { cn } from "cn";
import { type ComponentProps, useEffect, useRef } from "react";

type TiptapBubbleMenuProps = ComponentProps<typeof BubbleMenu>;

export type BubbleMenuPortalProps = Omit<
  TiptapBubbleMenuProps,
  "editor" | "appendTo" | "ref"
> & {
  editor: Editor;
};

const isRectVisibleIn = (rect: DOMRect, container: DOMRect) =>
  rect.bottom > container.top &&
  rect.top < container.bottom &&
  rect.right > container.left &&
  rect.left < container.right;

/**
 * The bubble menu, lifted out of the editor's DOM subtree.
 *
 * Tiptap v3 appends a bubble menu to `editor.view.dom.parentElement` and positions it
 * with `position: absolute`, so it inherits everything the surrounding app chrome does:
 * a dialog's `overflow-hidden` / `BlockScroll` clips it, and the drag handle — which the
 * drag-handle extension appends to that same parent at `z-index: 10` — paints over it.
 * Appending to `<body>` with a viewport-fixed strategy takes it out of both.
 *
 * That relocation costs the anchoring the inline element got for free, so the menu also
 * re-anchors on scroll: Tiptap only listens to `scroll` on `window` (configurable through
 * a single `scrollTarget`), which an inner scroll container never fires. The capture-phase
 * listener below sees every one of them, and fades the menu out while its anchor is
 * scrolled outside the container that moved.
 */
export const BubbleMenuPortal = ({
  editor,
  className,
  options,
  pluginKey,
  ...props
}: BubbleMenuPortalProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  // Tiptap captures the plugin key on first render and never re-reads it; mirror that
  // so `setMeta` below keeps addressing the plugin that is actually mounted.
  const pluginKeyRef = useRef(pluginKey ?? new PluginKey("bubbleMenuPortal"));

  useEffect(() => {
    const menuElement = menuRef.current;
    if (!menuElement) return;

    let frame = 0;
    // `hide()` removes the element, so `isConnected` is the plugin's own visibility —
    // no scroll work happens while no menu is open.
    const handleScroll = (event: Event) => {
      if (frame || editor.isDestroyed || !menuElement.isConnected) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        if (editor.isDestroyed || !menuElement.isConnected) return;

        const scroller = event.target instanceof Element ? event.target : null;
        // Only a scroller the editor sits inside can move the anchor. Anything else on
        // the page scrolling is none of this menu's business — and measuring against it
        // would read "anchor outside" and hide a menu that never moved.
        if (scroller && !scroller.contains(editor.view.dom)) return;

        const { from, to } = editor.state.selection;
        const isAnchorVisible =
          !scroller ||
          isRectVisibleIn(
            posToDOMRect(editor.view, from, to),
            scroller.getBoundingClientRect(),
          );

        // `visibility` is the only property to park it in: `show()`, `hide()` and the
        // plugin's own `updatePosition()` all rewrite it, so this can never get stuck
        // on a menu that Tiptap later re-shows. (`pointer-events` would: nothing in
        // the plugin resets it, leaving a visible menu that clicks fall straight through.)
        if (!isAnchorVisible) {
          menuElement.style.visibility = "hidden";
          return;
        }

        menuElement.style.visibility = "visible";
        editor.view.dispatch(
          editor.state.tr.setMeta(pluginKeyRef.current, "updatePosition"),
        );
      });
    };

    window.addEventListener("scroll", handleScroll, true);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [editor]);

  return (
    <BubbleMenu
      editor={editor}
      ref={menuRef}
      pluginKey={pluginKeyRef.current}
      appendTo={() => document.body}
      // Above the dialog layer (`z-50`), below the tooltip/popover layer (`z-[999]`).
      className={cn("z-[100]", className)}
      options={{ strategy: "fixed", ...options }}
      {...props}
    />
  );
};
